import { getCookie } from '../../utils/dom';
import { generateEventId, urlify, createSha256Hash } from '../../utils/string';

const receiptAnalytics = async (order, fromList = {}) => {
	if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
	const { items, totals, currency, discounts = {}, address = {} } = order;
	const uniqueEventID = generateEventId();
	const coupon = discounts.vouchers.reduce((acc, obj) => [...acc, obj.voucher], []).join(', ');

	let discount = 0;
	if (totals.totalDiscountPriceAsNumber) {
		const tax = items[0] ? items[0].priceEachAsNumber / items[0].priceEachWithoutTaxAsNumber : 1;
		// remove shipping then tax to get approx discount to send to CJ.
		discount = Math.round(((Math.abs(totals.totalDiscountPriceAsNumber) - totals.shippingPriceAsNumber) / tax) * 100) / 100;
		if (discount < 0) discount = 0;
	}

	window.dataLayer.push({ values: null });
	window.dataLayer.push({
		event: 'Purchase',
		event_id: uniqueEventID,
		values: {
			currency,
			content_type: 'product',
			contents: items.map((item) => ({
				id: item.item,
				quantity: item.quantity,
				item_price: item.priceEachAsNumber,
			})),
			num_items: items.length,
			value: totals.grandTotalPriceAsNumber,
		},
	});

	window.dataLayer.push({ ecommerce: null });
	window.dataLayer.push({
		event: 'PurchaseEvent',
		ecommerce: {
			currencyCode: currency,
			purchase: {
				actionField: {
					id: order.order,
					affiliation: '',
					revenue: totals.grandTotalPriceAsNumber,
					tax: totals.grandTotalPriceTaxAsNumber,
					shipping: totals.shippingPriceAsNumber,
					coupon,
				},
				products: items.map((item) => ({
					name: item.name,
					sku: item.sku,
					id: item.item,
					price: item.priceEachAsNumber,
					brand: item.brand,
					category: item.category,
					variant: item.variant,
					quantity: item.quantity,
				})),
			},
		},
	});

	window.dataLayer.push({ ecommerce: null });
	window.dataLayer.push({
		event: 'purchase_ga4',
		ecommerce: {
			'x-fb-event_id': uniqueEventID,
			currency,
			value: totals.grandTotalPriceAsNumber,
			tax: totals.grandTotalPriceTaxAsNumber,
			shipping: totals.shippingPriceAsNumber,
			transaction_id: order.order,
			coupon,
			items: items.map((item) => {
				const itemCategory = item.category && item.category.split('/').reduce((acc, category, j) => ({ ...acc, [`item_category${j > 0 ? j + 1 : ''}`]: category }), {});
				const itemId = item.item.slice(0, item.item.indexOf('-'));

				return {
					item_name: item.name,
					item_id: item.item,
					price: item.priceEachAsNumber,
					currency,
					...(itemCategory || {}),
					item_list_id: urlify(fromList[itemId] || ''),
					item_list_name: fromList[itemId] || '',
					item_brand: item.brand,
					item_variant: item.variant,
					quantity: item.quantity,
				};
			}),
			user_data: {
				email: address.email,
				address: {
					first_name: address.firstName,
					last_name: address.lastName,
					street: address.address1,
					postal_code: address.zipCode,
					region: address.state,
					city: address.city,
					country: address.country,
				},
			},
		},
	});

	/* TikTok */
	window.dataLayer.push({ values: null });
	window.dataLayer.push({
		event: 'CompletePaymentTikTok',
		values: {
			currency,
			contents: items.map((item) => ({
				content_id: item.item,
				content_type: 'product',
				content_name: item.name,
				quantity: item.quantity,
				price: item.priceEachAsNumber,
			})),
			quantity: totals.totalQuantity,
			value: totals.grandTotalPriceAsNumber,
		},
	});

	const discountExVat = totals.totalDiscountPriceAsNumber ? totals.totalDiscountPriceAsNumber / (1 + (parseInt(totals.taxPercent, 10) / 100)) : 0;
	const d = document.createElement('img');
	d.src = `https://www.shareasale.com/sale.cfm?tracking=${order.order}&amount=${(items.reduce((acc, item) => (acc + (item.priceEachWithoutTaxAsNumber * item.quantity)), 0) + discountExVat).toFixed(2)}&currency=${order.currency}&merchantID=136676&transtype=sale`;
	d.id = 'shareSalePixel';
	d.style.width = '1px';
	d.style.height = '1px';
	document.body.appendChild(d);

	/* CJ */
	const emailHash = await createSha256Hash(address.email);

	window.dataLayer.push({ cjData: null });
	window.dataLayer.push({
		event: 'CJConversionConfirmation',
		cjData: {
			emailHash,
			orderId: order.order,
			actionTrackerId: '451572',
			currency,
			cartSubtotal: `${(items.reduce((acc, item) => (acc + (item.priceEachWithoutTaxAsNumber * item.quantity)), 0) - discount).toFixed(2)}`,
			discount,
			coupon: urlify(coupon) || 0,
			customerCountry: address.country,
			items: items.map((item) => ({ unitPrice: item.priceEachWithoutTaxAsNumber, itemId: item.sku, quantity: item.quantity })),
		},
	});

	const eventTime = encodeURIComponent(new Date().toISOString()).replace(/[.]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);
	const cjImage = document.createElement('img');
	cjImage.src = `https://www.emjcd.com/u?CID=1570939&TYPE=451572&METHOD=S2S&SIGNATURE=&CJEVENT=${getCookie('cje') || ''}&eventTime=${eventTime}&OID=${order.order}&currency=${currency}&coupon=${urlify(coupon) || 0}&${items.reduce((acc, item, i) => [...acc, `ITEM${i + 1}=${encodeURIComponent(item.sku)}&AMT${i + 1}=${item.priceEachWithoutTaxAsNumber}&QTY${i + 1}=${item.quantity}`], []).join('&')}&discount=${discount}&customerCountry=${address.country}`;
	cjImage.id = 'cjPixel';
	cjImage.style.width = '1px';
	cjImage.style.height = '1px';
	document.body.appendChild(cjImage);
};

export default receiptAnalytics;
